import React, { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, TrendingUp, Gauge, PoundSterling } from 'lucide-react';
import { notify } from '@core/notifications/notify';
import PageInfoButton from '@/components/PageInfoButton';

interface CompanyUsage {
  companyId: number;
  companyName: string;
  planName: string;
  planId: string | null;
  aiRequests: number;
  aiLimit: number; // -1 = unlimited
  aiPercentage: number;
  parserDocuments: number;
  parserLimit: number;
  parserPercentage: number;
  evidenceUploads: number;
  // Optional: only populated for Yiontech-internal viewers.
  estimatedCostGbp?: number;
}

interface UsageResponse {
  period: string;
  totals: {
    aiRequests: number;
    parserDocuments: number;
    estimatedCostGbp?: number;
    activeCompanies: number;
  };
  companies: CompanyUsage[];
  costAssumptions?: {
    gbpPerAiRequest: number;
    gbpPerParserDoc: number;
  };
  /** Yiontech-only: margin projection showing current vs proposed revenue share. */
  marginProjection?: {
    topupGrossGbp: number;
    estimatedAnthropicGbp: number;
    current: { model: string; yiontechShareOnTopupGbp: number };
    proposed: { model: string; feePct: number; yiontechShareOnTopupGbp: number; upliftGbp: number };
    feeEnabledNow: boolean;
    note: string;
  };
  /** Yiontech-only: per-admin AI usage (system admins with no company_id). */
  systemAdminUsage?: Array<{
    userId: number;
    name: string;
    email: string;
    exempt: boolean;
    aiRequestsThisPeriod: number;
    parserStageCalls: number;
    parserTokens: number;
    parserCostGbp: number;
  }>;
  /** True when the viewer is a Yiontech staff account. */
  viewerIsYiontechInternal?: boolean;
}

const formatPct = (v: number) => `${Math.min(100, v)}%`;

const barColour = (pct: number) => {
  if (pct >= 95) return 'bg-red-500';
  if (pct >= 80) return 'bg-amber-500';
  if (pct >= 50) return 'bg-blue-500';
  return 'bg-emerald-500';
};

const AIUsageDashboard: React.FC = () => {
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(
    `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, '0')}`,
  );

  const load = async (p: string = period) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/ai-usage?period=${p}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success) setData(json);
      } else {
        notify.error('Failed to load AI usage data');
      }
    } catch {
      notify.error('Failed to load AI usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPeriodChange = (next: string) => {
    setPeriod(next);
    load(next);
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={32} />
      </div>
    );
  }

  const totals = data?.totals;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-3xl font-bold text-slate-900 text-left">AI Usage & Cost</h1>
              <PageInfoButton />
            </div>
            <p className="text-slate-500 text-sm mt-0.5 text-left">
              Per-company AI request and parser consumption, plan utilisation, and estimated monthly
              cost. Cost is a Sonnet/Opus-blended estimate — actual Anthropic invoice is the source of truth.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={period}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <button
              onClick={() => load(period)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>

        {/* Totals strip */}
        {totals && (
          <div className={`grid grid-cols-1 ${data?.viewerIsYiontechInternal ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 mb-6`}>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                <TrendingUp size={14} /> Active companies
              </div>
              <p className="text-3xl font-bold text-slate-900 mt-2">{totals.activeCompanies}</p>
              <p className="text-xs text-slate-400 mt-1">This month</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                <Gauge size={14} /> AI requests
              </div>
              <p className="text-3xl font-bold text-slate-900 mt-2">{totals.aiRequests.toLocaleString()}</p>
              {data?.viewerIsYiontechInternal && data?.costAssumptions && (
                <p className="text-xs text-slate-400 mt-1">
                  ≈ £{(totals.aiRequests * data.costAssumptions.gbpPerAiRequest).toFixed(2)} at
                  £{data.costAssumptions.gbpPerAiRequest}/req
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                <Gauge size={14} /> Parser docs
              </div>
              <p className="text-3xl font-bold text-slate-900 mt-2">{totals.parserDocuments.toLocaleString()}</p>
              {data?.viewerIsYiontechInternal && data?.costAssumptions && (
                <p className="text-xs text-slate-400 mt-1">
                  ≈ £{(totals.parserDocuments * data.costAssumptions.gbpPerParserDoc).toFixed(2)} at
                  £{data.costAssumptions.gbpPerParserDoc}/doc
                </p>
              )}
            </div>
            {data?.viewerIsYiontechInternal && typeof totals.estimatedCostGbp === 'number' && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <PoundSterling size={14} /> Estimated cost
                </div>
                <p className="text-3xl font-bold text-slate-900 mt-2">£{totals.estimatedCostGbp.toFixed(2)}</p>
                <p className="text-xs text-slate-400 mt-1">Platform-wide, this month · Yiontech only</p>
              </div>
            )}
          </div>
        )}

        {/* Yiontech-only: margin projection. Not rendered for Phil / EIS admins. */}
        {data?.viewerIsYiontechInternal && data?.marginProjection && (
          <div className="bg-slate-900 text-white rounded-2xl p-5 mb-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Yiontech internal · margin projection</p>
                <h2 className="text-lg font-semibold mt-1">Topup revenue share — current vs proposed SLA clause</h2>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${data.marginProjection.feeEnabledNow ? 'bg-emerald-500 text-emerald-50' : 'bg-slate-700 text-slate-300'}`}>
                {data.marginProjection.feeEnabledNow ? 'Fee active' : 'Fee disabled'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400">Topup gross this period</p>
                <p className="text-2xl font-bold mt-1">£{data.marginProjection.topupGrossGbp.toFixed(2)}</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-xs text-slate-400">{data.marginProjection.current.model}</p>
                <p className="text-2xl font-bold mt-1">£{data.marginProjection.current.yiontechShareOnTopupGbp.toFixed(2)}</p>
                <p className="text-[11px] text-slate-400 mt-1">Yiontech share on topups</p>
              </div>
              <div className="bg-emerald-950 border border-emerald-700 rounded-lg p-3">
                <p className="text-xs text-emerald-300">{data.marginProjection.proposed.model} ({data.marginProjection.proposed.feePct}%)</p>
                <p className="text-2xl font-bold mt-1">£{data.marginProjection.proposed.yiontechShareOnTopupGbp.toFixed(2)}</p>
                <p className="text-[11px] text-emerald-300 mt-1">
                  Yiontech share · +£{data.marginProjection.proposed.upliftGbp.toFixed(2)} uplift
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">{data.marginProjection.note}</p>
          </div>
        )}

        {/* Yiontech-only: per-admin AI usage. Shows Phil, Zoe, Yiontech
            staff and any other system admin. Exempt users are flagged;
            their counter still increments for visibility even though no
            cap is enforced on them. */}
        {data?.viewerIsYiontechInternal && data?.systemAdminUsage && data.systemAdminUsage.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Yiontech internal</p>
                <h2 className="text-base font-semibold text-slate-900">System admin AI usage this period</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Includes exempt accounts (no cap enforced). Counter resets monthly.
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Admin</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Assistant requests</th>
                    <th className="px-4 py-3 text-right">Parser AI calls</th>
                    <th className="px-4 py-3 text-right">Parser tokens</th>
                    <th className="px-4 py-3 text-right">Parser cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.systemAdminUsage.map((a) => (
                    <tr key={a.userId} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 text-left">{a.name}</p>
                        <p className="text-xs text-slate-400 text-left">#{a.userId}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-xs">{a.email}</td>
                      <td className="px-4 py-3">
                        {a.exempt ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            Exempt — uncapped
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                            Soft cap applies
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">
                        {a.aiRequestsThisPeriod.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-700">
                        {a.parserStageCalls.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-500 text-xs">
                        {a.parserTokens.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${a.parserCostGbp >= 10 ? 'text-red-600 font-semibold' : a.parserCostGbp >= 1 ? 'text-amber-700' : 'text-slate-900'}`}>
                        £{a.parserCostGbp.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Per-company table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Plan</th>
                  <th className="px-4 py-3 text-left">AI requests</th>
                  <th className="px-4 py-3 text-left">Parser docs</th>
                  {data?.viewerIsYiontechInternal && (
                    <th className="px-4 py-3 text-right">Est. cost</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {data?.companies.length === 0 ? (
                  <tr>
                    <td colSpan={data?.viewerIsYiontechInternal ? 5 : 4} className="px-4 py-10 text-center text-slate-400 text-sm">
                      No active companies this period.
                    </td>
                  </tr>
                ) : (
                  data?.companies.map((c) => (
                    <tr key={c.companyId} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900 text-left">{c.companyName}</p>
                        <p className="text-xs text-slate-400 text-left">#{c.companyId}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                          {c.planName}
                        </span>
                      </td>
                      <td className="px-4 py-3 min-w-[180px]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-slate-900 font-medium">{c.aiRequests.toLocaleString()}</span>
                          <span className="text-xs text-slate-400">
                            {c.aiLimit === -1 ? '∞' : `/ ${c.aiLimit.toLocaleString()}`}
                          </span>
                        </div>
                        {c.aiLimit !== -1 && (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${barColour(c.aiPercentage)} transition-all`}
                                style={{ width: formatPct(c.aiPercentage) }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 w-8 text-right">{c.aiPercentage}%</span>
                            {c.aiPercentage >= 80 && <AlertTriangle size={14} className="text-amber-500" />}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 min-w-[180px]">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-slate-900 font-medium">{c.parserDocuments.toLocaleString()}</span>
                          <span className="text-xs text-slate-400">
                            {c.parserLimit === -1 ? '∞' : `/ ${c.parserLimit.toLocaleString()}`}
                          </span>
                        </div>
                        {c.parserLimit !== -1 && (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${barColour(c.parserPercentage)} transition-all`}
                                style={{ width: formatPct(c.parserPercentage) }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 w-8 text-right">{c.parserPercentage}%</span>
                            {c.parserPercentage >= 80 && <AlertTriangle size={14} className="text-amber-500" />}
                          </div>
                        )}
                      </td>
                      {data?.viewerIsYiontechInternal && typeof c.estimatedCostGbp === 'number' && (
                        <td className="px-4 py-3 text-right font-mono text-slate-700">
                          £{c.estimatedCostGbp.toFixed(2)}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIUsageDashboard;
